<h2 mat-dialog-title>Nouvelle évaluation</h2>

<div *ngIf="backendError" class="backend-error-banner">
  <mat-icon>error_outline</mat-icon>
  <span>{{ backendError }}</span>
</div>

<mat-dialog-content class="dialog-content">
  <mat-stepper linear #stepper orientation="horizontal" (selectionChange)="onStepChange($event)">

    <!-- ══════════════════════════════════════════════════════════
        ÉTAPE 1 — Informations personnelles
    ══════════════════════════════════════════════════════════ -->
    <mat-step [stepControl]="step1Form" label="Informations">
      <form [formGroup]="step1Form" class="step-form">

        <div class="row-fields">
          <mat-form-field appearance="outline" color="accent" class="half-width">
            <mat-label>Nom</mat-label>
            <input matInput formControlName="nom" placeholder="Ex: Dupont" maxlength="120">
            <mat-hint align="end">{{ step1Form.get('nom')?.value?.length || 0 }}/120</mat-hint>
            <mat-error *ngIf="step1Form.get('nom')?.hasError('required')">Champ obligatoire</mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" color="accent" class="half-width">
            <mat-label>Prénom</mat-label>
            <input matInput formControlName="prenom" placeholder="Ex: Jean" maxlength="120">
            <mat-hint align="end">{{ step1Form.get('prenom')?.value?.length || 0 }}/120</mat-hint>
            <mat-error *ngIf="step1Form.get('prenom')?.hasError('required')">Champ obligatoire</mat-error>
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" color="accent" class="full-width">
          <mat-label>Application</mat-label>
          <mat-select formControlName="application">
            <mat-option *ngFor="let app of applications" [value]="app">{{ app }}</mat-option>
          </mat-select>
          <mat-error *ngIf="step1Form.get('application')?.hasError('required')">Champ obligatoire</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" color="accent" class="full-width uo">
          <mat-label>Unité organisationnelle</mat-label>
          <mat-select [(ngModel)]="uoPrincipaleId"
                      [ngModelOptions]="{standalone: true}"
                      (selectionChange)="onUoPrincipaleChange()">
            <mat-option *ngFor="let uo of unitesOrganisationnelles" [value]="uo.id">
              {{ uo.code }} — {{ uo.libelle }}
            </mat-option>
          </mat-select>
          <mat-hint>Toutes les catégories et compétences de cette UO seront ajoutées automatiquement</mat-hint>
        </mat-form-field>

        <div *ngIf="loadingUoPrincipale" class="loading-hint">
          <mat-icon class="spin">autorenew</mat-icon> Chargement des compétences...
        </div>

        <mat-form-field appearance="outline" color="accent" class="full-width">
          <mat-label>Date de démarrage de la mission</mat-label>
          <input matInput [matDatepicker]="picker" formControlName="dateDemarrage">
          <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
          <mat-hint>Fixée à la création — non modifiable ensuite</mat-hint>
          <mat-error *ngIf="step1Form.get('dateDemarrage')?.hasError('required')">Champ obligatoire</mat-error>
        </mat-form-field>
        
        <mat-form-field appearance="outline" color="accent" class="full-width">
          <mat-label>Date de fin de montée en compétence</mat-label>
          <input matInput [matDatepicker]="picker2" formControlName="dateFinMonteeCompetence">
          <mat-datepicker-toggle matIconSuffix [for]="picker2"></mat-datepicker-toggle>
          <mat-datepicker #picker2></mat-datepicker>
          <mat-hint>Fixée à la création — non modifiable ensuite</mat-hint>
          <mat-error *ngIf="step1Form.get('dateFinMonteeCompetence')?.hasError('required')">Champ obligatoire</mat-error>
        </mat-form-field>
       

        <div class="step-actions">
          <button mat-raised-button color="primary" matStepperNext
                  [disabled]="step1Form.invalid || !uoPrincipaleId">
            Suivant <mat-icon>arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- ══════════════════════════════════════════════════════════
        ÉTAPE 2 — Sélection des compétences
    ══════════════════════════════════════════════════════════ -->
    <mat-step label="Compétences">
      <div class="step-form">

        <!-- Sections UO additionnelles -->
        <div *ngFor="let section of uoSections; let i = index" class="uo-section">

          <div class="uo-section-header">
            <span class="uo-section-title">
              <mat-icon>add_business</mat-icon>
              {{ section.uoLibelle || 'UO supplémentaire ' + (i + 1) }}
            </span>
            <button mat-icon-button color="warn"
                    (click)="removeUoSection(i)"
                    matTooltip="Supprimer cette section">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <div class="row-fields">
            <mat-form-field appearance="outline" color="accent" class="half-width">
              <mat-label>Unité organisationnelle</mat-label>
              <mat-select [(ngModel)]="section.selectedUoId"
                          (selectionChange)="onUoChange(section)">
                <mat-option *ngFor="let uo of getAvailableUos(section)" [value]="uo.id">
                  {{ uo.code }} — {{ uo.libelle }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" color="accent" class="half-width">
              <mat-label>Catégorie</mat-label>
              <mat-select [(ngModel)]="section.selectedCategorieId"
                          (selectionChange)="onCategorieChange(section)"
                          [disabled]="!section.selectedUoId">
                <mat-option *ngFor="let cat of section.categoriesFiltrees" [value]="cat.id">
                  {{ cat.nom }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <div *ngIf="section.competencesFiltrees.length > 0" class="competence-chips">
            <div *ngFor="let comp of section.competencesFiltrees"
                 class="competence-chip"
                 [class.already-added]="isAlreadyAdded(comp.id)"
                 (click)="addCompetence(comp)">
              <mat-icon *ngIf="isAlreadyAdded(comp.id)">check</mat-icon>
              <mat-icon *ngIf="!isAlreadyAdded(comp.id)">add</mat-icon>
              <span>{{ comp.libelle }}</span>
            </div>
          </div>

          <div *ngIf="section.selectedCategorieId && section.competencesFiltrees.length === 0"
               class="empty-hint">
            Aucune compétence disponible pour cette catégorie.
          </div>

        </div>

        <button mat-stroked-button color="primary" class="btn-add-uo" (click)="addUoSection()">
          <mat-icon>add</mat-icon> Ajouter des compétences d'une autre UO
        </button>

        <!-- Liste des compétences sélectionnées -->
        <div class="section-header" style="margin-top: 20px;">
          <span class="section-title">Compétences sélectionnées ({{ competencesSelectionnees.length }})</span>
        </div>

        <div *ngIf="competencesSelectionnees.length === 0" class="empty-hint">
          Choisissez une UO principale pour charger automatiquement ses compétences.
        </div>

        <div class="preview-table" *ngIf="competencesSelectionnees.length > 0">
          <table>
            <thead>
              <tr>
                <th>Catégorie</th>
                <th>Compétence</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let comp of competencesSelectionnees">
                <td class="td-categorie">{{ comp.categorieNom }}</td>
                <td>{{ comp.libelle }}</td>
                <td class="td-action">
                  <button mat-icon-button color="warn"
                          (click)="removeCompetence(comp.id)"
                          matTooltip="Retirer">
                    <mat-icon>delete</mat-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="step-actions">
          <button mat-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon> Précédent
          </button>
          <button mat-raised-button color="primary" matStepperNext
                  [disabled]="competencesSelectionnees.length === 0">
            Suivant <mat-icon>arrow_forward</mat-icon>
          </button>
        </div>
      </div>
    </mat-step>

    <!-- ══════════════════════════════════════════════════════════
        ÉTAPE 3 — Récapitulatif
    ══════════════════════════════════════════════════════════ -->
    <mat-step label="Récapitulatif">
      <div class="step-form">

        <div class="recap-infos">
          <div class="recap-row">
            <span class="recap-label">Collaborateur</span>
            <span class="recap-value">{{ step1Form.get('prenom')?.value }} {{ step1Form.get('nom')?.value }}</span>
          </div>
          <div class="recap-row">
            <span class="recap-label">Application</span>
            <span class="recap-value">{{ step1Form.get('application')?.value }}</span>
          </div>
          <div class="recap-row">
            <span class="recap-label">Démarrage</span>
            <span class="recap-value">{{ step1Form.get('dateDemarrage')?.value | date:'dd/MM/yyyy' }}</span>
          </div>
        </div>

        <div class="section-header" style="margin-top: 16px;">
          <span class="section-title">
            {{ competencesSelectionnees.length }} compétences ·
            Statut : <span class="badge-brouillon">BROUILLON</span>
          </span>
        </div>

        <div class="preview-table">
          <table>
            <thead>
              <tr>
                <th>Catégorie</th>
                <th>Compétence</th>
                
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let comp of competencesSelectionnees">
                <td class="td-categorie">{{ comp.categorieNom }}</td>
                <td>{{ comp.libelle }}</td>
                
              </tr>
            </tbody>
          </table>
        </div>

        <div class="step-actions">
          <button mat-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon> Précédent
          </button>
        </div>
      </div>
    </mat-step>

  </mat-stepper>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button type="button" (click)="onCancel()">Annuler</button>
  <button mat-raised-button color="primary"
          type="button"
          (click)="onSubmit()"
          [disabled]="step1Form.invalid || competencesSelectionnees.length === 0 || !step3Visited || isSubmitting"
          [matTooltip]="!step3Visited ? 'Parcourez les 3 étapes avant de créer l\'évaluation' : ''">
    Créer l'évaluation
  </button>
</mat-dialog-actions>